<script setup lang="ts">
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { computed, onMounted, ref, watch } from 'vue'
  import {
    QuestionnairesService,
    type SuperUserOrganisation as Organisation,
    type SuperUserQuestionnaire as Questionnaire
  } from '@/types/generated'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger
  } from '@/components/ui/dialog'
  import { useRoute } from 'vue-router'
  import { EyeIcon, PlusIcon, BoltIcon } from '@heroicons/vue/24/outline'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { useQuestionnaireEditorStore } from '@/store/admin/questionnaire/questionnaireEditor'
  import QuestionnaireSection from '@/components/admin/questionnaire/editor/QuestionnaireEditorSection.vue'
  import QuestionnaireEditorCustomerSelect from '@/components/admin/questionnaire/editor/QuestionnaireEditorCustomerSelect.vue'
  import { genericErrorToast, saveSuccessToast } from '@/lib/toasts'
  import { useTranslation } from '@/composables/useTranslation'
  import { Separator } from '@/components/ui/separator'
  import FileField from '@/components/admin/form/FileField.vue'
  import { toast } from '@/components/ui/toast'
  import FloatingSaveButton from '@/components/admin/questionnaire/editor/QuestionnaireEditorSaveButton.vue'

  const { t } = useTranslation({
    en: {
      create: 'Create',
      createWithAI: 'Create questionnaire with AI',
      creatingQuestionnaire: 'Creating questionnaire, this may take a while...',
      enterUserPrompt: 'Enter AI prompt',
      questionnaireCreated: 'Questionnaire created with AI',
      questionnaireCreatedDescription:
        'The questionnaire has been created with AI, it is however not yet saved',
      uploadFile: 'Upload file',
      uploadToCreateQuestionnaire: 'Upload a pdf/word file to create a questionnaire using AI',
      uploadHint: 'Upload a pdf/word file or drag and drop it here',
      userPrompt: 'AI Prompt',
      or: 'or'
    },
    de: {
      create: 'Erstellen',
      createWithAI: 'Fragebogen mit KI erstellen',
      creatingQuestionnaire: 'Fragebogen wird erstellt, dies kann einige Zeit dauern...',
      enterUserPrompt: 'KI Prompt eingeben',
      questionnaireCreated: 'Fragebogen mit KI erstellt',
      questionnaireCreatedDescription:
        'Der Fragebogen wurde mit KI erstellt, ist jedoch noch nicht gespeichert',
      uploadFile: 'Datei hochladen',
      uploadToCreateQuestionnaire:
        'Laden Sie eine PDF-/Word-Datei hoch, um einen Fragebogen mit KI zu erstellen',
      uploadHint: 'Laden Sie eine PDF-/Word-Datei hoch oder ziehen Sie sie hierher',
      userPrompt: 'KI Prompt',
      or: 'oder'
    }
  })

  const route = useRoute()
  const questionnaireEditorStore = useQuestionnaireEditorStore()
  const questionnaire = computed<Questionnaire | null>(() => questionnaireEditorStore.questionnaire)
  const organisations = computed<Organisation[]>(() => questionnaireEditorStore.organisations)
  const categoryOptions = computed<Option[]>(() => questionnaireEditorStore.categoryOptions)
  const lawFieldsOptions = computed<Option[]>(() => questionnaireEditorStore.lawFieldsOptions)
  const lawTopicOptions = computed<Option[]>(() => questionnaireEditorStore.lawTopicOptions)
  const uploadedFile = ref<File | null>(null)
  const userPrompt = ref('')
  const search = ref('')

  const isModalOpen = ref(false)
  const isLoading = ref(false)
  const hasChanges = ref(false)

  watch(
    questionnaire,
    (newValue, oldValue) => {
      if (oldValue !== null || newValue === null) {
        // Do not set hasChanges on initial load when old value is null
        hasChanges.value = true
      }
    },
    { deep: true }
  )

  onMounted(async () => {
    await questionnaireEditorStore.init(route.params.uuid as string)
  })

  const saveQuestionnaire = async () => {
    try {
      await QuestionnairesService.questionnairesUpdate({
        uuid: questionnaire.value!.uuid,
        requestBody: questionnaire.value!
      })
      saveSuccessToast()
    } catch (error) {
      console.error('Error saving questionnaire:', error)
      genericErrorToast()
    }
  }

  const sendFile = async () => {
    try {
      isLoading.value = true
      await QuestionnairesService.questionnairesGenerateCreate({
        uuid: questionnaire.value!.uuid,
        formData: {
          file: uploadedFile.value!,
          prompt: userPrompt.value ?? ''
        }
      }).then((response) => {
        // Update the questionnaire with the generated data
        questionnaire.value.title = response.title || questionnaire.value.title
        questionnaire.value.description = response.description || questionnaire.value.description
        questionnaire.value.category = response.category || questionnaire.value.category
        questionnaire.value.law_field = response.law_field || questionnaire.value.law_field
        questionnaire.value.law_topic = response.law_topic || questionnaire.value.law_topic
        questionnaire.value.schema = response.schema || questionnaire.value.schema
        toast({
          title: t('questionnaireCreated'),
          description: t('questionnaireCreatedDescription'),
          status: 'info',
          duration: 3000
        })
      })
    } catch (error) {
      isModalOpen.value = false
      isLoading.value = false
      console.error('Error creating questionnaire with AI:', error)
      genericErrorToast()
    } finally {
      uploadedFile.value = null
      isModalOpen.value = false
      isLoading.value = false
    }
  }
</script>

<template>
  <div v-if="questionnaire">
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex flex-wrap items-center justify-between gap-5 overflow-hidden">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Questionnaires', to: ROUTES.ADMIN.QUESTIONNAIRES.QUESTIONNAIRE_LIST },
            {
              title: `Details - ${questionnaire.title}`,
              to: ROUTES.ADMIN.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(
                ':uuid',
                questionnaire.uuid as string
              )
            }
          ]"
        />
        <div class="flex items-center justify-end gap-x-2">
          <Dialog v-model:open="isModalOpen">
            <DialogTrigger as-child>
              <button
                class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-4 py-3
                  text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
                  focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
                @click="isModalOpen = true"
              >
                <BoltIcon class="h-4 w-4" />
                {{ t('createWithAI') }}
              </button>
            </DialogTrigger>
            <DialogContent
              v-if="isLoading"
              class="flex flex-col items-center"
            >
              <DialogTitle></DialogTitle>
              <div
                class="text-surface mt-2 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid
                  border-current border-e-transparent align-[-0.125em]
                  motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              >
                <span
                  class="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0
                    ![clip:rect(0,0,0,0)]"
                >
                  Loading...
                </span>
              </div>
              <DialogDescription>
                <h3 class="text-lg">{{ t('creatingQuestionnaire') }}</h3>
              </DialogDescription>
            </DialogContent>
            <DialogContent
              v-else
              class="sm:max-w-[425px]"
            >
              <DialogHeader>
                <DialogTitle>{{ t('createWithAI') }}</DialogTitle>
                <DialogDescription>
                  {{ t('uploadToCreateQuestionnaire') }}
                </DialogDescription>
              </DialogHeader>
              <FileField
                v-model="uploadedFile"
                name="file"
                class="overflow-hidden"
                :allowed-file-extensions="['pdf', 'doc', 'docx']"
                :hint="t('uploadHint')"
              />
              <Separator
                class="my-4"
                :label="t('or')"
              />
              <TextAreaField
                v-model="userPrompt"
                name="user_prompt"
                :placeholder="t('enterUserPrompt')"
              />
              <DialogFooter>
                <button
                  class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-4 py-3
                    text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:bg-purple-700 focus:outline-none
                    disabled:pointer-events-none disabled:opacity-50"
                  type="submit"
                  :disabled="!uploadedFile && !userPrompt"
                  @click="sendFile"
                >
                  {{ t('create') }}
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <a
            :href="`/client/portal/questionnaire/preview/${questionnaire.uuid}`"
            target="_blank"
            class="inline-flex items-center gap-x-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm
              font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
              disabled:pointer-events-none disabled:opacity-50"
          >
            <EyeIcon class="size-4 shrink-0" />
            Preview
          </a>
        </div>
      </div>
    </div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <div class="flex flex-col rounded-xl border bg-white p-4 shadow-sm md:p-5">
        <h3 class="text-lg font-bold text-gray-800">Questionnaire Details</h3>
        <div class="mb-4 w-full md:w-1/2">
          <InputField
            v-model="questionnaire.title"
            label="Title"
            name="title"
          />
        </div>
        <div class="mb-4 w-full md:w-1/2">
          <TextAreaField
            v-model="questionnaire.description"
            label="Description"
            name="description"
            rows="2"
          />
        </div>
        <div class="mb-4 w-full md:w-1/2">
          <SelectField
            v-model="questionnaire.category"
            label="Category"
            name="category"
            :options="categoryOptions"
          />
        </div>
        <div class="mb-4 w-full md:w-1/2">
          <SelectField
            v-model="questionnaire.law_field"
            label="Law field"
            name="law_field"
            :options="lawFieldsOptions"
          />
        </div>
        <div class="mb-4 w-full md:w-1/2">
          <SelectField
            v-model="questionnaire.law_topic"
            label="Law Topic"
            name="law_field"
            :options="lawTopicOptions"
          />
        </div>
      </div>
      <div class="flex flex-col rounded-xl border bg-white p-4 shadow-sm md:p-5">
        <h3 class="text-lg font-bold text-gray-800">Customers</h3>
        <div class="mb-4 w-full pt-4 md:w-1/2">
          <div class="inline-flex rounded-lg bg-gray-100 p-1">
            <button
              type="button"
              class="active rounded-md border border-transparent p-2 text-xs font-medium text-gray-800
                hover:border-gray-400 focus:border-gray-400 focus:outline-none"
              :class="questionnaire.enabled_all_orgs ? 'bg-white' : ''"
              @click="questionnaire.enabled_all_orgs = true"
            >
              All customers
            </button>
            <button
              type="button"
              class="rounded-md border border-transparent p-2 text-xs font-medium text-gray-800 hover:border-gray-400
                focus:border-gray-400 focus:outline-none"
              :class="!questionnaire.enabled_all_orgs ? 'bg-white' : ''"
              @click="questionnaire.enabled_all_orgs = false"
            >
              Selected customers
            </button>
          </div>
        </div>
        <div v-if="!questionnaire.enabled_all_orgs">
          <div class="mb-4 w-full md:w-1/2">
            <InputField
              v-model="search"
              name="search"
              placeholder="Search for customers"
            />
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <input
                  id="optional"
                  class="ms-2 h-5 w-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  type="checkbox"
                  name="optional"
                  :checked="questionnaireEditorStore.isEnabledForAllCustomOrganisations"
                  @change="questionnaireEditorStore.toggleEnableAllCustomOrganisations"
                />
                <label
                  class="ml-2 block text-sm font-medium text-gray-700"
                  for="optional"
                >
                  Select all
                </label>
              </div>
              <span class="text-sm text-gray-600">
                {{ questionnaire.enabled_custom_orgs.length }} selected
              </span>
            </div>

            <div class="scrollable-container">
              <QuestionnaireEditorCustomerSelect
                v-for="org in organisations"
                :key="org.uuid"
                :search="search"
                :uuid="org.uuid"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="rounded-xl border bg-white p-4 shadow-sm md:p-5">
        <h3 class="text-lg font-bold text-gray-800">Questions</h3>
        <div class="mb-4 mt-4">
          <div class="flex justify-start">
            <button
              type="button"
              class="inline-flex items-center gap-x-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm
                font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
                disabled:pointer-events-none disabled:opacity-50"
              @click="questionnaireEditorStore.addSection(0)"
            >
              <PlusIcon class="size-4 shrink-0" />
              Add Section
            </button>
          </div>
          <div
            v-for="(section, index) in questionnaire.schema"
            :key="section.uuid"
          >
            <QuestionnaireSection :uuid="section.uuid" />
            <div class="flex justify-start">
              <button
                type="button"
                class="inline-flex items-center gap-x-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm
                  font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
                  disabled:pointer-events-none disabled:opacity-50"
                @click="questionnaireEditorStore.addSection(index + 1)"
              >
                <PlusIcon class="size-4 shrink-0" />
                Add Section
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <FloatingSaveButton
      :has-changes="hasChanges"
      :on-save="saveQuestionnaire"
    />
  </div>
</template>

<style scoped>
  .scrollable-container {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
    border-radius: 0.375rem;
    margin-top: 0.5rem;
  }

  /* For Webkit browsers like Chrome/Safari */
  .scrollable-container::-webkit-scrollbar {
    width: 8px;
  }

  .scrollable-container::-webkit-scrollbar-track {
    background: #f7fafc;
  }

  .scrollable-container::-webkit-scrollbar-thumb {
    background-color: #cbd5e0;
    border-radius: 4px;
    border: 2px solid #f7fafc;
  }

  /* For Internet Explorer */
  .scrollable-container {
    -ms-overflow-style: -ms-autohiding-scrollbar;
  }
</style>
